import { AccountDetail } from "@garther/interfaces";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClients } from "./context";

export const referralKeys = {
  all: ["referral"] as const,
  stats: (orgId?: string) => [...referralKeys.all, "stats", ...(orgId ? [orgId] : [])] as const
};

export function useReferralStats(options: { enabled?: boolean; orgId?: string } = {}) {
  const { referralApiClient } = useApiClients();
  const { enabled = true, orgId } = options;

  return useQuery({
    queryKey: referralKeys.stats(orgId),
    queryFn: () => referralApiClient.getStats(orgId),
    enabled,
    staleTime: 10 * 60 * 1000
  });
}

export function useReferralInvalidation() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: referralKeys.all }),
    invalidateUserStats: () => queryClient.invalidateQueries({ queryKey: referralKeys.stats(), exact: true }),
    invalidateOrgStats: (orgId: string) =>
      queryClient.invalidateQueries({ queryKey: referralKeys.stats(orgId), exact: true })
  };
}

export function useVerifyReferralCodeMutation(
  options: {
    onSuccess?: (result: AccountDetail, code: string) => void;
    onError?: (error: unknown) => void;
  } = {}
) {
  const { referralApiClient } = useApiClients();
  return useMutation({
    mutationFn: (code: string) => referralApiClient.verifyCode(code),
    onSuccess: (result, code) => {
      options.onSuccess?.(result, code);
    },
    onError: (error) => options.onError?.(error)
  });
}
